import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { AIMMapServer, PotentialAcquisitionFieldsMapViewServer, PropertyBoundariesMapServer } from './services';
import LabelClass from '@arcgis/core/layers/support/LabelClass';
import { alpha, darken } from '@mantine/core';
import { propertyBoundaryRenderer } from './utils/renderers';
import { propertyColors } from './utils/constants';

/**
 * Unique identifier string for the potential acquisition fields feature layer.
 */
export const POTENTIAL_ACQUISITION_FIELDS_LAYER_ID = 'potential-acquisition-fields-layer';

/**
 * Creates an ArcGIS FeatureLayer for potential acquisition field data from the Leased.PotentialAcquisitionLayers service.
 *
 * @param title - Configuration object containing an optional title used in the layer's display name.
 * @returns A new `@arcgis/core/layers/FeatureLayer` instance configured for the `MapServer/3` layer of the PotentialAcquisitionLayers service.
 */
/**
 * Creates an ArcGIS `FeatureLayer` for potential acquisitions fields,
 * configured with the provided title and mapped to the potential
 * acquisitions map server URL.
 *
 * @param title - The display title to prefix onto " Fields" in the layer's title.
 * @returns A configured `FeatureLayer` instance for potential acquisitions fields.
 */
export const createPotentialAcquisitionsFieldLayer = ({ title }:{title:string|undefined}) =>
  new FeatureLayer({
    url: PotentialAcquisitionFieldsMapViewServer.url,
    id: POTENTIAL_ACQUISITION_FIELDS_LAYER_ID,
    title: `${title} Fields`,
    outFields: ['*'],
    minScale: 0,
    legendEnabled:true,
    maxScale: 0,
    visible: true
  });

/**
 * Creates a FeatureLayer for owned property boundaries from the Jarvis IGP GIS Layers service with bloom glow effects and labels.
 *
 * @returns A new `@arcgis/core/layers/FeatureLayer` instance pointing at layer index 14 of the IGP_GIS_Layers MapServer, with a boundary renderer, labeled NAME, and an effect that blooms when zoomed below 5,000,000 scale.
 */
/**
 * Creates the owned-property boundary layer — a styled FeatureLayer
 * pointing at the Property Boundaries MapServer with bloom effects, labels,
 * and a filter excluding inactive statuses.
 *
 * @returns A configured `FeatureLayer` for owned property boundaries.
 */
export const createBoundaryLayer = ()=> new FeatureLayer({
  title: 'Owned Properties',
  labelsVisible: true,
  url: PropertyBoundariesMapServer.url,
  outFields: [
    'NAME',
    'ABBV_NAME',
    'MgmtType',
    'ManagedBy',
    'Division',
    'ACRES',
    'POLITICAL_DIVISION',
    'OP_TYPE',
    'FARMNUM',
    'OBJECTID',
    'BusinessUnitNumber'
  ],
  objectIdField: 'OBJECTID',
  minScale: 5000000,
  effect: [
    // { scale: 10000_000, value: 'opacity(0)' },
    { scale: 5000000, value: 'bloom(1, 0.5px ,0) opacity(10%)' },
    { scale: 1500000, value: 'bloom(0, 0px ,0) opacity(100%)' }
  ],
  maxScale: 0,
  definitionExpression: "STATUS<>'Inactive' or Status = null",
  labelingInfo: [
    new LabelClass({
      labelExpressionInfo: { expression: '$feature.NAME' },
      minScale: 1500000,
      maxScale: 0,
      symbol: {
        // borderLineSize: 5,
        yoffset: -15,
        type: 'text',
        color: 'white',
        haloSize: 2,
        font: {
          // autocast as new Font()
          size: 12,
          weight: 'bold'
        },
        haloColor: 'rgba(0,0,0,.55)'
      }
    })
  ],
  renderer:propertyBoundaryRenderer,
  // minScale: 100000000,
  id: 'boundaries-layer'
})

/**
 * Creates a FeatureLayer for potential acquisitions (AIM) with investment-themed styling and multi-scale labeling.
 *
 * @returns A new `@arcgis/core/layers/FeatureLayer` instance pointing at the AIM OpportunityEditMap/MapServer/0 layer, styled with semi-transparent investment-colored fills and bloom glow effects when zoomed in.
 */
/**
 * Creates a styled FeatureLayer for potential acquisitions — a thin wrapper
 * around the AIM MapServer with bloom effects and specialized labeling at
 * different zoom scales.
 *
 * @returns A configured `FeatureLayer` for potential acquisitions.
 */
export const createPotentialAcquisitionsLayer = ()=> new FeatureLayer({
  url: AIMMapServer.Acquisitions.url,
  outFields: ['*'],
  id: 'acquisitionLayerId',
  title: 'Potential Acquisitions',
  maxScale: 0,
  minScale: 0,
  definitionExpression: `OpportunityID is not null`,// and STATUS in ('Active','Purchase Pending','Sale Pending',null)`,
  renderer: {
    type: 'simple',
    symbol: {
      type: 'simple-fill',
      color: alpha(darken(propertyColors.investment, 0.95), 0.15),
      outline: {
        color: propertyColors.investment,
        width: 2
      }
    }
  },
  effect: [
    { scale: 5000000, value: 'bloom(2, 0.5px ,0) opacity(10%)' },
    { scale: 1_500_000, value: 'bloom(0, 0px ,0) opacity(100%)' }
  ],
  labelingInfo: [
    {
      //pins
      maxScale: 1_500_000,
      labelExpressionInfo: { expression: '$feature.NAME' },
      symbol: {
        type: 'text', // autocasts as new TextSymbol()
        color: '#7A003C',
        text: '\ue61d', // esri-icon-map-pin
        font: {
          // autocasts as new Font()
          size: 36,
          family: 'CalciteWebCoreIcons' // Esri Icon Font
        }
      }
    },
    {
      maxScale: 1_249_200,
      minScale: 1_500_000,
      labelExpressionInfo: { expression: '$feature.NAME' },
      symbol: { type: 'text', color: 'white', haloColor: 'black', haloSize: 2, yoffset: 10 }
    },
    {
      maxScale: 0,
      minScale: 1_249_200,
      labelExpressionInfo: {
        expression:
          'Concatenate($feature.NAME, TextFormatting.NewLine, Floor($feature.ACRES*100)/100, " AC");'
      }
    }
  ]
});